import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { CheckCircle, ErrorOutlined, Refresh, ReportProblem } from "@mui/icons-material";
import { useDashboardOverview, useRefreshDashboard } from "@/api/hooks/dashboard";
import { useUserPermissions } from "@/api/hooks/permissions";
import { formatSiteDisplayId } from "@/api/labels";
import { formatQuarter } from "@/utils/format";
import { brand } from "@/theme/brandColors";
import type { DeliveryStatus } from "@/api/types";

const STATUS_BG: Record<DeliveryStatus, string> = {
  fresh: brand.forest,
  stale: brand.orange,
  missing: brand.crimson,
};

const STATUS_LABEL: Record<DeliveryStatus, string> = {
  fresh: "Fresh",
  stale: "Stale",
  missing: "Missing",
};

const STATUS_ICON: Record<DeliveryStatus, typeof CheckCircle> = {
  fresh: CheckCircle,
  stale: ReportProblem,
  missing: ErrorOutlined,
};

function StatusPill({ status }: { status: DeliveryStatus }) {
  const Icon = STATUS_ICON[status];
  return (
    <Chip
      size="small"
      icon={<Icon fontSize="small" sx={{ color: `${brand.pearl} !important` }} />}
      label={STATUS_LABEL[status]}
      sx={{
        bgcolor: STATUS_BG[status],
        color: brand.pearl,
        fontWeight: 600,
        // Fixed width so the column reads as a tidy stack of pills
        // instead of jagged edges where "Missing" runs wider than
        // "Fresh".
        width: 110,
        justifyContent: "flex-start",
      }}
    />
  );
}

export function DashboardOverviewPage() {
  const query = useDashboardOverview();
  const refresh = useRefreshDashboard();
  const permissions = useUserPermissions();
  const isAdmin = permissions.data?.is_clinops_admin ?? false;

  if (query.isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (query.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Could not load dashboard: {(query.error as Error).message}
        </Alert>
      </Box>
    );
  }
  if (!query.data) return null;

  const { sites, rollup } = query.data;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          Data Delivery
        </Typography>
        {isAdmin && (
          <Tooltip title="Re-query Athena for every linked site and refresh the table">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  refresh.isPending
                    ? <CircularProgress size={14} />
                    : <Refresh fontSize="small" />
                }
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
              >
                {refresh.isPending ? "Refreshing…" : "Refresh stats"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <Typography color="text.secondary" sx={{ mb: 1, fontSize: 14 }}>
        Latest encounter date per site and whether they're sending
        unstructured (notes) data. Status is keyed off the latest
        encounter date: <strong>fresh</strong> = previous quarter or
        later, <strong>stale</strong> = older than the previous
        quarter, <strong>missing</strong> = no data delivered.
      </Typography>
      {refresh.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Refresh failed: {(refresh.error as Error).message}
        </Alert>
      )}
      {refresh.data && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Refreshed {refresh.data.sites.length} site
          {refresh.data.sites.length === 1 ? "" : "s"}
          {refresh.data.sites.filter((s) => s.error).length > 0 && (
            <> · {refresh.data.sites.filter((s) => s.error).length} with errors</>
          )}
        </Alert>
      )}
      <Box sx={{ mb: 3 }} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <RollupCard label="Total sites" value={rollup.total_sites} />
        <RollupCard label="Fresh" value={rollup.fresh_sites} accent={brand.forest} />
        <RollupCard label="Stale" value={rollup.stale_sites} accent={brand.orange} />
        <RollupCard label="Missing" value={rollup.missing_sites} accent={brand.crimson} />
        <RollupCard
          label="With unstructured"
          value={rollup.sites_with_unstructured}
          accent={brand.aqua}
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 0, overflowX: "auto" }}>
        <Box sx={{
          px: 2, py: 1.5,
          bgcolor: brand.ocean, color: brand.pearl,
          borderBottom: 1, borderColor: "divider",
        }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: brand.pearl }}>
            Sites · {sites.length}
          </Typography>
        </Box>
        {sites.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            No sites configured yet.
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 140 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>
                  Latest encounter
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 110 }}>
                  Quarter
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>
                  Latest notes
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 110 }}>
                  Notes quarter
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 200 }}>
                  Stats refreshed
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.map((s) => (
                <TableRow key={s.site_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {s.site_name}
                    </Typography>
                    {s.display_id !== null ? (
                      <Typography variant="caption" color="text.secondary">
                        Site ID {formatSiteDisplayId(s.display_id)}
                      </Typography>
                    ) : (
                      <Tooltip title="Site must have a display_id to be queried">
                        <Typography variant="caption" color="warning.main">
                          No site ID
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusPill status={s.status} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {s.latest_encounter_date ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {formatQuarter(s.latest_encounter_date)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {s.latest_notes_date ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                    {formatQuarter(s.latest_notes_date)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                    {s.stats_refreshed_at
                      ? new Date(s.stats_refreshed_at).toLocaleString()
                      : "Never"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}

function RollupCard({
  label, value, accent,
}: { label: string; value: number; accent?: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, flex: 1,
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: accent ?? "text.primary" }}>
        {value}
      </Typography>
    </Paper>
  );
}
